import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import {
  Cartesian2,
  EllipsoidGeodesic,
  defined,
  getTimestamp
} from "cesium/Build/Cesium/Cesium";

import useUnitSystem from "hooks/useUnitSystem";
import { normalizeSystem } from "utils/units/format";
import { getUnit, toBase } from "utils/units/registry";

import useStyles from "./styles";

const LENGTH_FAMILY = "length";
const THROTTLE_MS = 250;
const MAX_BAR_WIDTH = 80;

const METRIC_DISTANCES_M = [
  1, 2, 3, 5, 10, 20, 30, 50, 100, 200, 300, 500, 1000, 2000, 3000, 5000, 10000,
  20000, 30000, 50000, 100000, 200000, 300000, 500000, 1000000, 2000000,
  3000000, 5000000, 10000000, 20000000, 30000000, 50000000
];

const IMPERIAL_FEET = [
  1, 2, 3, 5, 10, 20, 30, 50, 100, 200, 300, 500, 1000, 2000, 3000, 5000
];

const IMPERIAL_MILES = [
  1, 2, 3, 5, 10, 20, 30, 50, 100, 200, 300, 500, 1000, 2000, 3000, 5000, 10000,
  20000, 30000, 50000
];

const IMPERIAL_DISTANCES = [
  ...IMPERIAL_FEET.map(value => ({
    value,
    unitId: "ft",
    meters: toBase(value, "ft", LENGTH_FAMILY)
  })),
  ...IMPERIAL_MILES.map(value => ({
    value,
    unitId: "mi",
    meters: toBase(value, "mi", LENGTH_FAMILY)
  }))
].sort((a, b) => a.meters - b.meters);

const EMPTY_LEGEND = { label: null, barWidth: null };

function formatMetricLabel(distanceMeters, locale) {
  if (distanceMeters >= 1000) {
    const km = distanceMeters / 1000;
    return `${km.toLocaleString(locale)} ${getUnit(LENGTH_FAMILY, "km").symbol}`;
  }

  return `${distanceMeters.toLocaleString(locale)} ${getUnit(LENGTH_FAMILY, "m").symbol}`;
}

function formatImperialLabel({ value, unitId }, locale) {
  const symbol = getUnit(LENGTH_FAMILY, unitId).symbol;
  return `${value.toLocaleString(locale)} ${symbol}`;
}

function pickLegendValues(pixelDistanceMeters, system, locale) {
  if (!Number.isFinite(pixelDistanceMeters) || pixelDistanceMeters <= 0) {
    return null;
  }

  const normalizedSystem = normalizeSystem(system);

  if (normalizedSystem === "imperial") {
    for (let i = IMPERIAL_DISTANCES.length - 1; i >= 0; i -= 1) {
      const entry = IMPERIAL_DISTANCES[i];
      if (entry.meters / pixelDistanceMeters < MAX_BAR_WIDTH) {
        return {
          label: formatImperialLabel(entry, locale),
          barWidth: (entry.meters / pixelDistanceMeters) | 0
        };
      }
    }

    return null;
  }

  for (let i = METRIC_DISTANCES_M.length - 1; i >= 0; i -= 1) {
    const distance = METRIC_DISTANCES_M[i];
    if (distance / pixelDistanceMeters < MAX_BAR_WIDTH) {
      return {
        label: formatMetricLabel(distance, locale),
        barWidth: (distance / pixelDistanceMeters) | 0
      };
    }
  }

  return null;
}

function measurePixelDistanceMeters(scene, geodesic) {
  const width = scene.canvas.clientWidth;
  const height = scene.canvas.clientHeight;

  const leftRay = scene.camera.getPickRay(
    new Cartesian2((width / 2) | 0, height - 1)
  );
  const rightRay = scene.camera.getPickRay(
    new Cartesian2(1 + ((width / 2) | 0), height - 1)
  );

  const globe = scene.globe;
  const leftPosition = globe.pick(leftRay, scene);
  const rightPosition = globe.pick(rightRay, scene);

  if (!defined(leftPosition) || !defined(rightPosition)) {
    return null;
  }

  const leftCartographic =
    globe.ellipsoid.cartesianToCartographic(leftPosition);
  const rightCartographic =
    globe.ellipsoid.cartesianToCartographic(rightPosition);

  geodesic.setEndPoints(leftCartographic, rightCartographic);
  return geodesic.surfaceDistance;
}

// Prefer the compass container so the legend shares the compass' exact
// positioning context (it tracks panel/window width identically). Fall back to
// the outer navigation container if the compass is disabled.
const COMPASS_CONTAINER_SELECTOR = "#navigationDiv";
const NAV_CONTAINER_SELECTOR = ".cesium-widget-cesiumNavigationContainer";

/**
 * Unit-aware distance scale legend for the Cesium 3D globe.
 * Replaces the cesium-navigation-es6 distance legend with metric/imperial support.
 *
 * @param {{ viewer: import("cesium").Viewer|null }} props
 * @returns {JSX.Element|null}
 */
const CesiumDistanceLegend = ({ viewer }) => {
  const classes = useStyles();
  const { system, locale } = useUnitSystem();

  const [navContainer, setNavContainer] = useState(null);
  const [legend, setLegend] = useState(EMPTY_LEGEND);
  const legendRef = useRef(EMPTY_LEGEND);
  const lastUpdateRef = useRef(0);
  const geodesicRef = useRef(new EllipsoidGeodesic());
  const systemRef = useRef(system);
  const localeRef = useRef(locale);

  systemRef.current = system;
  localeRef.current = locale;

  const setLegendIfChanged = useCallback(next => {
    if (
      next.label === legendRef.current.label &&
      next.barWidth === legendRef.current.barWidth
    ) {
      return false;
    }

    legendRef.current = next;
    setLegend(next);
    return true;
  }, []);

  useEffect(() => {
    lastUpdateRef.current = 0;
    viewer?.scene?.requestRender();
  }, [system, locale, viewer]);

  useEffect(() => {
    const widgetContainer = viewer?.cesiumWidget?.container;
    if (!widgetContainer) {
      setNavContainer(null);
      return undefined;
    }

    const findNavContainer = () =>
      widgetContainer.querySelector(COMPASS_CONTAINER_SELECTOR) ||
      widgetContainer.querySelector(NAV_CONTAINER_SELECTOR);

    const existing = findNavContainer();
    if (existing) {
      setNavContainer(existing);
      return undefined;
    }

    const observer = new MutationObserver(() => {
      const container = findNavContainer();
      if (container) {
        setNavContainer(container);
        observer.disconnect();
      }
    });

    observer.observe(widgetContainer, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [viewer]);

  useEffect(() => {
    if (!viewer) {
      return undefined;
    }

    const scene = viewer.scene;
    const geodesic = geodesicRef.current;

    const onPostRender = () => {
      const now = getTimestamp();
      if (now < lastUpdateRef.current + THROTTLE_MS) {
        return;
      }

      lastUpdateRef.current = now;

      const pixelDistance = measurePixelDistanceMeters(scene, geodesic);
      if (pixelDistance == null) {
        if (setLegendIfChanged(EMPTY_LEGEND)) {
          scene.requestRender();
        }
        return;
      }

      const picked = pickLegendValues(
        pixelDistance,
        systemRef.current,
        localeRef.current
      );

      const next = picked
        ? { label: picked.label, barWidth: picked.barWidth }
        : EMPTY_LEGEND;

      if (setLegendIfChanged(next)) {
        scene.requestRender();
      }
    };

    const removeListener = scene.postRender.addEventListener(onPostRender);
    scene.requestRender();

    return () => {
      removeListener();
    };
  }, [viewer, setLegendIfChanged]);

  if (!legend.label || !legend.barWidth || !navContainer) {
    return null;
  }

  return createPortal(
    <div className={classes.root} aria-hidden>
      <div className={classes.label}>{legend.label}</div>
      <div className={classes.scaleBar} style={{ width: legend.barWidth }} />
    </div>,
    navContainer
  );
};

CesiumDistanceLegend.propTypes = {
  viewer: PropTypes.shape({
    cesiumWidget: PropTypes.shape({
      container: PropTypes.object
    }),
    scene: PropTypes.shape({
      postRender: PropTypes.shape({
        addEventListener: PropTypes.func
      }),
      requestRender: PropTypes.func
    })
  })
};

export default CesiumDistanceLegend;
